const getServiceFileTimestamp = (file) => {
  const timestamp = Date.parse(file?.created_at);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const getKycRunId = (file) => {
  const metadata = file?.metadata ?? {};
  return (
    file?.workflow_run_id ??
    file?.workflowRunId ??
    file?.workflow_id ??
    file?.workflowId ??
    file?.run_id ??
    file?.runId ??
    file?.session_id ??
    file?.sessionId ??
    metadata.workflow_run_id ??
    metadata.workflowRunId ??
    metadata.workflow_id ??
    metadata.workflowId ??
    metadata.run_id ??
    metadata.runId ??
    metadata.session_id ??
    metadata.sessionId
  );
};

const getDocumentType = (file) => {
  const metadata = file?.metadata ?? {};
  const type =
    metadata.document_type ??
    metadata.id_type ??
    metadata.type ??
    file?.document_type;

  return typeof type === "string"
    ? type
        .toLowerCase()
        .trim()
        .replace(/_(front|back)$/, "")
    : null;
};

const getDocumentNumber = (file) => {
  const metadata = file?.metadata ?? {};
  const number = [
    metadata.personal_number,
    metadata.dni_nie,
    metadata.document_number,
    metadata.passport_id,
    metadata.id_number,
  ].find((value) => value !== undefined && value !== null && value !== "");

  return number === undefined ? null : String(number).toUpperCase().trim();
};

const getDocumentSide = (file) => {
  const metadata = file?.metadata ?? {};
  const side = String(metadata.side ?? "")
    .toLowerCase()
    .trim();
  if (side === "front" || side === "back") return side;

  const type = String(metadata.type ?? "").toLowerCase();
  if (type.endsWith("_front")) return "front";
  if (type.endsWith("_back")) return "back";
  return null;
};

/**
 * Keep only files linked to the most recent KYC run. National identity cards
 * have front/back service-file records; when the API doesn't provide a shared
 * run ID, retain the opposite side only when it is the same document type and
 * was recorded within two minutes of the newest side.
 */
export const getLatestKycServiceFiles = (serviceFiles = []) => {
  const kycFiles = (Array.isArray(serviceFiles) ? serviceFiles : []).filter(
    (file) =>
      String(file?.service ?? "")
        .toUpperCase()
        .trim() === "KYC"
  );

  if (kycFiles.length <= 1) return kycFiles;

  const latestFile = [...kycFiles].sort((a, b) => {
    const aTimestamp = getServiceFileTimestamp(a);
    const bTimestamp = getServiceFileTimestamp(b);
    if (aTimestamp === null) return bTimestamp === null ? 0 : 1;
    if (bTimestamp === null) return -1;
    return bTimestamp - aTimestamp;
  })[0];

  const latestRunId = getKycRunId(latestFile);
  if (latestRunId !== undefined && latestRunId !== null) {
    const filesInLatestRun = kycFiles.filter(
      (file) => String(getKycRunId(file) ?? "") === String(latestRunId)
    );
    if (filesInLatestRun.length > 0) return filesInLatestRun;
  }

  const latestTimestamp = getServiceFileTimestamp(latestFile);
  if (latestTimestamp === null) return [latestFile];

  const latestType = getDocumentType(latestFile);
  const latestSide = getDocumentSide(latestFile);
  const latestDocumentNumber = getDocumentNumber(latestFile);
  if (!latestType) return [latestFile];

  return kycFiles.filter((file) => {
    const timestamp = getServiceFileTimestamp(file);
    const type = getDocumentType(file);
    if (timestamp === null || type !== latestType) return false;

    const documentNumber = getDocumentNumber(file);
    if (
      latestDocumentNumber &&
      documentNumber &&
      documentNumber !== latestDocumentNumber
    ) {
      return false;
    }

    if (timestamp === latestTimestamp) return true;
    if (Math.abs(timestamp - latestTimestamp) > 2 * 60 * 1000) return false;

    const side = getDocumentSide(file);
    return latestSide && side && side !== latestSide;
  });
};
